// components/CaseStudyMosaic.tsx
"use client";
import Image from "next/image";
import clsx from "clsx";
import SectionHeader from "@/components/shared/headers/SectionHeader";
import { TransitionLink } from "@/components/shared/TransitionLink";
import { useDict } from "@/components/providers/LocaleProvider";

export type BrandTile = {
  id: string;
  src: string;
  alt: string;
  title?: string;
  aspect?: "square" | "landscape" | "portrait";
  className: string; // grid col spans per breakpoint
};

export const brandTiles: BrandTile[] = [
  {
    id: "hero",
    src: "/assets/design/freshgreen.avif",
    alt: "Fresh Green Image",
    title: "Fresh Green",
    aspect: "landscape",
    // full width on xs (2 cols), 6-col grid on md+, hero takes 4 cols
    className: "col-span-1 md:col-span-3 lg:col-span-4"
  },
  {
    id: "palette",
    src: "/assets/design/caeliora1.avif",
    alt: "Caeliora Image",
    title: "Caeliora",
    aspect: "portrait",
    className: "col-span-1 md:col-span-2"
  },
  {
    id: "packaging",
    src: "/assets/design/revitalift.avif",
    alt: "Revitalift Image",
    title: "Revitalift",
    aspect: "square",
    className: "col-span-1 md:col-span-2"
  },
  {
    id: "editorial",
    src: "/assets/design/friedchicken.avif",
    alt: "KFC Fried Chicken",
    title: "KFC Fried Chicken",
    aspect: "landscape",
    className: "col-span-1 md:col-span-2 lg:col-span-2"
  },
  // {
  //   id: "social",
  //   src: "/assets/design/doublecream1.avif",
  //   alt: "Double Cream Image",
  //   title: "Double Cream",
  //   aspect: "square",
  //   className: "col-span-1 md:col-span-2 lg:col-span-4"
  // },
  // {
  //   id: "apparel",
  //   src: "/assets/design/burger.avif",
  //   alt: "Burger Image",
  //   title: "Burger",
  //   aspect: "landscape",
  //   className: "col-span-1 md:col-span-3 lg:col-span-2"
  // },
  {
    id: "eclipsed",
    src: "/assets/design/eclipsed.avif",
    alt: "Eclipsed",
    title: "Eclipsed",
    aspect: "square",
    className: "col-span-1 md:col-span-2 "
  },
  {
    id: "tags",
    src: "/assets/design/headphone1.avif",
    alt: "Headphone Image",
    title: "Headphone",
    aspect: "portrait",
    className: "col-span-1 md:col-span-2 lg:col-span-4"
  }
];

export function DesignGrid() {
  const t = useDict();
  return (
    // no background color on the section
    <section className="">
      <SectionHeader
        title={t.home.designs.title}
        description={t.home.designs.description}
      />

      <section className="w-full lg:col-span-4 mt-10">
        <div
          className={clsx(
            "grid gap-3 md:gap-6",
            // 2 columns on mobile, 6 on md+ so we can fine-tune spans
            " grid-cols-1 sm:grid-cols-2 md:grid-cols-7 lg:grid-cols-8"
          )}
        >
          {brandTiles.map((t) => (
            <figure
              key={t.id}
              className={clsx(
                "relative rounded-lg overflow-hidden h-[300px] lg:h-[400px] shadow-sm",
                t.className //
              )}
            >
              <Image
                src={t.src}
                alt={t.alt}
                fill
                sizes="(min-width: 768px) 33vw, 100vw"
                className="rounded-lg object-cover hover:scale-105 transition duration-500 ease-in-out"
                priority={t.id === "hero"}
              />
              {t.title && (
                <figcaption className="pointer-events-none absolute inset-x-3 bottom-3 md:inset-x-4 md:bottom-4">
                  <span className="inline-flex rounded-md px-2 py-1 text-[11px] md:text-xs font-medium bg-black/35 text-white">
                    {t.title}
                  </span>
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </section>

      <section className=" mt-8 w-full flex justify-center items-center">
        <TransitionLink
        href="/designs"
        className="place-self-center bg-primary/80 text-white rounded-full px-6 py-3 hover:scale-105 transition duration-500 ease-in-out cursor-pointer shadow-[0_0_15px_#846dff]"
      >
        {t.home.designs.viewAll}
      </TransitionLink>
      </section>

    </section>
  );
}
